// ============================================================
// AppShell — the authenticated app layout.
// ------------------------------------------------------------
// DI-Navigation Menu design implementation:
//   • Desktop (md+): collapsible left Sidebar (280px → 72px) +
//     compact top header (logo + role switcher + language +
//     theme + notifications + avatar). Main content fills the
//     remaining space.
//   • Mobile (<md): compact top header (menu btn + logo +
//     notifications + avatar) + 5-slot BottomNav (Home, Orders,
//     Track, Alerts, Profile) + role switcher bar above content.
//
// All navigation is driven by the centralized `src/lib/navigation.ts`
// config so every surface stays in sync.
// ============================================================

import { useAuthStore } from "@/store/auth";
import { useUiStore } from "@/store/ui";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Bell,
  Moon,
  Sun,
  Monitor,
  PanelLeft,
  Settings,
  LogOut,
  User as UserIcon,
} from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { LanguageSwitcher } from "@/components/i18n/language-switcher";
import { SyncIndicator } from "@/components/i18n/sync-indicator";
import { SearchTrigger } from "@/components/features/search-palette";
import { TenantSwitcher } from "@/components/views/admin-tenants";
import { useTranslation } from "react-i18next";
import { AppFooter } from "@/components/layout/app-footer";
import { SetupBanner } from "@/components/layout/setup-banner";
import { PwaInstallBanner } from "@/components/layout/pwa-install-banner";
import { LiveNotifications } from "@/components/features/live-status";
import { Logo } from "@/components/brand/logo";
import { BrandWordmark } from "@/components/brand/wordmark";
import { Sidebar, MobileSidebar } from "@/components/layout/sidebar";
import { RoleSwitcher } from "@/components/layout/role-switcher";
import {
  MODULES,
  getPrimaryModules,
  getActiveModule,
  getDashboardRoute,
  getModuleRoute,
  moduleLabel,
  type NavModule,
} from "@/lib/navigation";

// ============================================================
// AppHeader — compact top bar.
// ------------------------------------------------------------
// Mobile:  menu btn • logo • spacer • bell • avatar
// Desktop: logo+wordmark • spacer • role switcher • sync •
//          language • theme • bell • avatar menu
// ============================================================
export function AppHeader({ onMenuClick }: { onMenuClick: () => void }) {
  const { user, logout } = useAuthStore();
  const route = useUiStore((s) => s.route);
  const navigate = useUiStore((s) => s.navigate);
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();

  const [resolved, setResolved] = useState<"light" | "dark">(() => {
    if (typeof document === "undefined") return "light";
    return document.documentElement.classList.contains("dark") ? "dark" : "light";
  });
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setResolved(document.documentElement.classList.contains("dark") ? "dark" : "light");
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const { data: notifData } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.notifications.list(),
    refetchInterval: 15000,
  });
  const unread = notifData?.data?.notifications?.filter((n) => !n.read).length ?? 0;

  if (!user) return null;
  const isAdmin = user.isAdmin;

  const ThemeIcon = theme === "system" ? Monitor : resolved === "dark" ? Moon : Sun;
  const themeLabel = theme === "system" ? t("theme.system") : resolved === "dark" ? t("theme.dark") : t("theme.light");

  return (
    <header
      className="sticky top-0 z-40 w-full border-b border-border/60 bg-background/85 backdrop-blur-lg"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <div className="flex h-14 items-center gap-1.5 px-3">
        {/* Mobile menu button — opens sidebar drawer */}
        <Button variant="ghost" size="icon" className="h-9 w-9 md:hidden" onClick={onMenuClick} aria-label={t("nav.openMenu")}>
          <PanelLeft className="h-4 w-4" />
        </Button>

        {/* Logo + wordmark — mobile only.
            On desktop the sidebar already shows the brand, so repeating it
            here would be redundant. Clicking navigates to the dashboard. */}
        <button
          onClick={() => navigate(getDashboardRoute(user))}
          className="flex items-center gap-2 md:hidden"
          aria-label={t("nav.homeAria")}
        >
          <Logo className="h-7 w-7 shrink-0" />
          <BrandWordmark className="text-base" />
        </button>

        <div className="flex-1" />

        {/* Global search trigger (Ctrl/Cmd+K) — desktop only */}
        <div className="hidden md:block">
          <SearchTrigger />
        </div>

        {/* Multi-tenant switcher — admin only */}
        {isAdmin && (
          <div className="hidden lg:block">
            <TenantSwitcher />
          </div>
        )}

        {/* Role switcher — non-admin only (admin has no switchable roles) */}
        {!isAdmin && <RoleSwitcher variant="dropdown" />}

        {/* Sync — desktop only (status indicator, not essential on mobile) */}
        <div className="hidden md:block">
          <SyncIndicator />
        </div>

        {/* Language switcher — always visible (compact icon on mobile) */}
        <LanguageSwitcher compact />

        {/* Theme toggle — always visible. On mobile this is the ONLY way
            to switch light/dark mode, so it must not be hidden. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              aria-label={`${t("theme.label")} (${themeLabel})`}
            >
              <ThemeIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuLabel className="text-xs text-muted-foreground">{t("theme.label")}</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => setTheme("light")}>
              <Sun className="mr-2 h-4 w-4" /> {t("theme.light")}
              {theme === "light" && <span className="ml-auto text-xs">●</span>}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme("dark")}>
              <Moon className="mr-2 h-4 w-4" /> {t("theme.dark")}
              {theme === "dark" && <span className="ml-auto text-xs">●</span>}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme("system")}>
              <Monitor className="mr-2 h-4 w-4" /> {t("theme.system")}
              {theme === "system" && <span className="ml-auto text-xs">●</span>}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Notifications — always visible */}
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9"
          onClick={() => navigate("app/notifications")}
          aria-label={t("nav.notificationsAria")}
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-accent-foreground">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>

        {/* Profile avatar menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="rounded-full" aria-label={t("nav.profileMenu")}>
              <Avatar className="h-8 w-8 border border-border">
                <AvatarFallback className="bg-primary/10 text-xs font-bold text-primary">
                  {user.name.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="flex flex-col">
              <span className="font-semibold">{user.name}</span>
              <span className="text-xs font-normal text-muted-foreground">{user.email}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate(getModuleRoute(user, MODULES.profile))}>
              <UserIcon className="mr-2 h-4 w-4" /> {moduleLabel(MODULES.profile, t)}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate(getModuleRoute(user, MODULES.settings))}>
              <Settings className="mr-2 h-4 w-4" /> {moduleLabel(MODULES.settings, t)}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => logout()}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="mr-2 h-4 w-4" /> {t("profile.signOut")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

// ============================================================
// BottomNav — mobile-only 5-slot navigation.
// ------------------------------------------------------------
// Per the DI-Navigation design: Home, Orders, Track, Alerts, Profile.
// The active slot gets a primary-colored indicator bar + icon scale.
// Badges show unread counts on Alerts.
// ============================================================
export function BottomNav() {
  const { user } = useAuthStore();
  const route = useUiStore((s) => s.route);
  const navigate = useUiStore((s) => s.navigate);
  const { t } = useTranslation();

  const { data: notifData } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.notifications.list(),
    refetchInterval: 15000,
  });
  const unread = notifData?.data?.notifications?.filter((n) => !n.read).length ?? 0;

  if (!user) return null;

  const primary = getPrimaryModules(user);
  const active = getActiveModule(route.path);

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 border-t border-border/60 bg-background/95 backdrop-blur-lg md:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      aria-label={t("nav.mobilePrimary")}
    >
      <div className="mx-auto flex max-w-md items-stretch justify-around">
        {primary.map((m) => {
          const isActive = active?.id === m.id || (m.id === "dashboard" && !active);
          const badge = m.id === "alerts" && unread > 0 ? unread : null;
          return (
            <NavButton
              key={m.id}
              module={m}
              active={isActive}
              badge={badge}
              onClick={() => navigate(getModuleRoute(user, m))}
              label={moduleLabel(m, t)}
            />
          );
        })}
      </div>
    </nav>
  );
}

// ============================================================
// NavButton — single primary slot in the bottom nav.
// ============================================================
function NavButton({
  module: m,
  active,
  badge,
  onClick,
  label,
}: {
  module: NavModule;
  active: boolean;
  badge?: number | null;
  onClick: () => void;
  label: string;
}) {
  const Icon = m.icon;
  return (
    <button
      onClick={onClick}
      className={`relative flex flex-1 flex-col items-center gap-0.5 py-1.5 text-[11px] font-medium transition active:scale-95 ${
        active ? "text-primary" : "text-muted-foreground"
      }`}
      aria-current={active ? "page" : undefined}
    >
      {active && <span className="absolute top-0 h-0.5 w-8 rounded-full bg-primary" />}
      <div className="relative">
        <Icon className={`h-5 w-5 transition ${active ? "scale-110" : ""}`} />
        {badge != null && (
          <span className="absolute -right-2 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[9px] font-bold text-accent-foreground">
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </div>
      <span className="line-clamp-1 leading-none">{label}</span>
    </button>
  );
}

// ============================================================
// AppShell — wraps every authenticated screen.
// ------------------------------------------------------------
// Desktop:  [Sidebar | (Header / main / footer)]
// Mobile:   [Header / main / footer / BottomNav]
// Role switching is done via the header dropdown (all viewports).
// ============================================================
export function AppShell({ children }: { children: React.ReactNode }) {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  return (
    <div className="flex min-h-screen">
      {/* Desktop sidebar (sticky, full height) */}
      <Sidebar />

      {/* Main column — header + content + footer.
          min-w-0 is CRITICAL: without it the flex item expands to fit its
          widest child (e.g. sponsored-ad strips with fixed-width cards),
          blowing out the page width on mobile. */}
      <div className="flex min-h-screen min-w-0 flex-1 flex-col">
        <AppHeader onMenuClick={() => setMobileSidebarOpen(true)} />

        {/* min-w-0 + overflow-hidden: constrains horizontal width so the
            fixed-width children below (ad strips, store cards) clip and
            scroll internally instead of overflowing the viewport.
            pb-32 (128px) on mobile clears the fixed BottomNav (56px) +
            PwaInstallBanner (60px) + safe-area so the last content card
            can scroll above both. */}
        <main className="mx-auto w-full max-w-5xl min-w-0 flex-1 space-y-4 overflow-x-hidden px-3 py-4 pb-32 md:pb-8">
          <SetupBanner />
          {children}
        </main>
        <AppFooter />
      </div>

      {/* Mobile bottom nav */}
      <BottomNav />
      <PwaInstallBanner />

      {/* Realtime: subscribes to the user's personal notification channel
          and invalidates the notification query when a new one arrives. */}
      <LiveNotifications />

      {/* Mobile sidebar drawer */}
      <MobileSidebar open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen} />
    </div>
  );
}
